import {Mode, Plan, uploadCapBytes} from "./gating";

/**
 * The plan quota has no applicable meter: either the mode is uncapped (instant/P2P) or the plan's
 * hosted upload allowance is unbounded (`uploadCapBytes` maps a null entitlement to Infinity).
 * Callers render the "∞ Unlimited" line instead of a bar.
 */
export interface UnlimitedUsage {
	kind: "unlimited";
}

/** The plan quota applies: a finite allowance the transfer consumes some fraction of. */
export interface MeteredUsage {
	kind: "metered";
	/** The plan's hosted upload allowance, echoed so views need not re-query gating for the label. */
	capBytes: number;
	/** Allowance left, floored at 0 — an over-cap transfer reads "0 remaining", never negative. */
	remainingBytes: number;
	/** Whole percent of the allowance consumed, capped at 100 so the bar cannot overflow its track. */
	pct: number;
}

export type Usage = UnlimitedUsage | MeteredUsage;

/**
 * Quota derivation for the sender's usage meter: resolves the plan allowance via the canonical
 * `gating.uploadCapBytes` and decides whether the meter applies at all.
 *
 * Instant (P2P) is not metered: past the free cap a FREE sender needs a paid plan and a paid plan needs nothing
 * (kitecorp/transferify-ui#3025, kitecorp/transferify-api-service#3005), and the over-limit strip, not this
 * meter, says so. A non-finite cap means the plan advertises unlimited hosted upload.
 */
export function resolveUsage(mode: Mode, plan: Plan, totalBytes: number): Usage {
	const capBytes = uploadCapBytes(plan);
	if (mode === "instant" || !Number.isFinite(capBytes)) {
		return {kind: "unlimited"};
	}
	return {
		kind: "metered",
		capBytes,
		remainingBytes: remainingFor(capBytes, totalBytes),
		pct: pctFor(capBytes, totalBytes),
	};
}

/**
 * The metered arithmetic, split from the cap lookup so both clamps and the cap===0 edge are testable
 * against literal caps without reaching through a plan.
 */
function remainingFor(capBytes: number, totalBytes: number): number {
	return Math.max(0, capBytes - totalBytes);
}

function pctFor(capBytes: number, totalBytes: number): number {
	return Math.min(100, Math.round((totalBytes / capBytes) * 100));
}

/** @internal Exposed for tests that need to drive the metered math at a specific cap. */
export const usageMath = {remainingFor, pctFor};
